import { Box, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useState } from 'react';
import { camelCaseOrSnakeCaseToWords } from 'src/data-capture/merge-review/ReviewHelperFunctions';
import { FieldTypeMetadata } from 'src/data-capture/types';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import classes from '../../data-capture/merge-review/MergeReviewPage.module.css';
import { DateField } from './fields/DateField';
import { DefaultField } from './fields/DefaultField';
import { ListField } from './fields/ListField';
import { MoneyField } from './fields/MoneyField';
import { PercentageField } from './fields/PercentageField';
import { SelectField } from './fields/SelectField';
import { TextField } from './fields/TextField';
import { BaseFieldProps } from './fields/types';
import { ViewFormField } from 'src/non-financial-data/components/fields/ViewFormField';

type FormFieldProp = {
  form: UseFormReturnType<FormValuesType>;
  fieldName: string;
  entityMetadata: FieldTypeMetadata;
  disabled?: boolean;
  highlightNumber?: number;
  parentFieldName?: string;
  label?: string;
  isEditing?: boolean;
  readModePrepend?: string;
  readModeAppend?: string;
  hideLabel?: boolean;
  onChange?: (value: unknown, fieldName: string) => void;
};

export const FormFieldNew = ({
  form,
  fieldName,
  entityMetadata,
  disabled = false,
  highlightNumber = undefined,
  parentFieldName = undefined,
  label = undefined,
  isEditing: isEditingProp,
  readModePrepend = '',
  readModeAppend = '',
  hideLabel = false,
  onChange = undefined,
}: FormFieldProp) => {
  const formFieldName = parentFieldName ? `${parentFieldName}.${fieldName}` : fieldName;
  const { isEditing: isEditingContext } = useDynamicUI();
  const isEditing = isEditingProp ?? isEditingContext;
  const { hide } = useConditionalHideCheck({
    form,
    formFieldName,
    entityMetadata,
  });

  const fieldLabel = label || entityMetadata.label || camelCaseOrSnakeCaseToWords(fieldName);
  const rightValue = highlightNumber ? <HighlightNumber number={highlightNumber} /> : null;
  function encrichedOnChange(value: unknown, fieldName: string) {
    if (onChange) {
      onChange(value, fieldName);
    }
    form.getInputProps(formFieldName).onChange(value);
  }

  const commonProps: BaseFieldProps = {
    form,
    fieldName,
    entityMetadata,
    disabled,
    highlightNumber,
    parentFieldName,
    label: hideLabel ? undefined : fieldLabel,
    formFieldName,
    fieldLabel: hideLabel ? undefined : fieldLabel,
    rightValue,
    readModePrepend,
    readModeAppend,
    onChange: encrichedOnChange,
  };

  if (hide) {
    return null;
  }

  return isEditing ? (
    <EditFormField<FormValuesType> {...commonProps} />
  ) : (
    <ViewFormField {...commonProps} />
  );
};

type ConditionalHideCheckProps = {
  form: UseFormReturnType<FormValuesType>;
  formFieldName: string;
  entityMetadata: FieldTypeMetadata;
};
const useConditionalHideCheck = ({
  form,
  formFieldName,
  entityMetadata,
}: ConditionalHideCheckProps) => {
  const [hide, setHide] = useState(false);

  try {
    const getDependingFieldPath = () => {
      if (!entityMetadata.dependentOnField) {
        return undefined;
      }

      const splitPath = formFieldName.split('.');
      splitPath[splitPath.length - 1] = entityMetadata.dependentOnField;
      return splitPath.join('.');
    };
    const dependingFieldPath = getDependingFieldPath();
    const initialHideValue = dependingFieldPath
      ? form.getInputProps(dependingFieldPath).defaultValue !== 'true'
      : false;

    if (hide !== initialHideValue) {
      setHide(initialHideValue);
    }

    if (dependingFieldPath) {
      form.watch(dependingFieldPath, ({ value }) => {
        // @ts-expect-error Mantine doesn't account for field value path
        setHide('true' !== value);
      });
    }

    return { hide };
  } catch (e) {
    if (e instanceof Error) {
      throw new Error(`${e.message} for field ${formFieldName}`, { cause: e });
    }
    throw e;
  }
};

function EditFormField<T extends Record<string, unknown>>(props: BaseFieldProps<T>) {
  const { entityMetadata, highlightProtected = false } = props;
  const fieldType = entityMetadata.type;

  if (highlightProtected && entityMetadata.protected) {
    return (
      <DefaultField
        {...props}
        tooltip={
          "This field is protected from data upload edits. If you want to change this value, please do it via the client's profile page."
        }
        disabled
      />
    );
  }

  if (fieldType === 'SELECT' || fieldType === 'BOOLEAN') {
    return <SelectField<T> {...props} />;
  }

  if (fieldType === 'MONEY') {
    return <MoneyField<T> {...props} />;
  }

  if (fieldType === 'DATE') {
    return <DateField<T> {...props} />;
  }

  if (fieldType === 'PERCENTAGE') {
    return <PercentageField<T> {...props} />;
  }

  if (fieldType === 'LIST') {
    return <ListField<T> {...props} itemType={entityMetadata.itemType} />;
  }

  if (fieldType === 'TEXT') {
    return <TextField<T> {...props} />;
  }

  if (fieldType === 'NUMBER' || fieldType === 'STRING') {
    return <DefaultField<T> {...props} />;
  }

  fieldType satisfies never;
  return null;
}

type HighlightNumberProps = {
  number: number;
};

const HighlightNumber = ({ number }: HighlightNumberProps) => {
  return (
    <Box className={classes.highlightNumberCircle}>
      <Text className={classes.highlightNumber}>{number}</Text>
    </Box>
  );
};
